/* eslint-disable no-console */

import {checkLogForPII} from './LoggerPIIRestrictedObjects';

export type LogLevel = 'info' | 'warn' | 'error' | 'trace' | 'log' | 'debug' | 'file-only';
export type LogCallback = typeof log | null;

let log: (name: string, level: LogLevel, message: string, ...args: any[]) => void = () => {};
export function setLogFn(fn: typeof log) {
  log = fn;
}

let nativeLog: (name: string, level: LogLevel, message: string, ...args: any[]) => void = () => {};
export function setNativeLogFn(fn: typeof log) {
  nativeLog = fn;
}

export class Logger {
  name: string;
  nativeLoggerEnabled: boolean;

  constructor(name = 'default') {
    this.name = name;
    this.nativeLoggerEnabled = false;
  }

  enableNativeLogger(enabled: boolean) {
    this.nativeLoggerEnabled = enabled;
  }

  logDangerously = (message: string, ...args: any[]) => {
    log(this.name, 'log', message, ...args);
    if (this.nativeLoggerEnabled) {
      nativeLog?.(this.name, 'log', message, ...args);
    }
  };

  log = (message: string, ...args: any[]) => {
    checkLogForPII(message, ...args);
    log(this.name, 'log', message, ...args);
    if (this.nativeLoggerEnabled) {
      nativeLog?.(this.name, 'log', message, ...args);
    }
  };

  verboseDangerously = (message: string, ...args: any[]) => {
    log(this.name, 'debug', message, ...args);
    if (this.nativeLoggerEnabled) {
      nativeLog?.(this.name, 'debug', message, ...args);
    }
  };

  verbose = (message: string, ...args: any[]) => {
    checkLogForPII(message, ...args);
    log(this.name, 'debug', message, ...args);
    if (this.nativeLoggerEnabled) {
      nativeLog?.(this.name, 'debug', message, ...args);
    }
  };

  info = (message: string, ...args: any[]) => {
    checkLogForPII(message, ...args);
    log(this.name, 'info', message, ...args);
    if (this.nativeLoggerEnabled) {
      nativeLog?.(this.name, 'info', message, ...args);
    }
  };

  warn = (message: string, ...args: any[]) => {
    checkLogForPII(message, ...args);
    log(this.name, 'warn', message, ...args);
    if (this.nativeLoggerEnabled) {
      nativeLog?.(this.name, 'warn', message, ...args);
    }
  };

  error = (message: string, ...args: any[]) => {
    checkLogForPII(message, ...args);
    log(this.name, 'error', message, ...args);
    if (this.nativeLoggerEnabled) {
      nativeLog?.(this.name, 'error', message, ...args);
    }
  };

  trace = (message: string, ...args: any[]) => {
    log(this.name, 'trace', message, ...args);
    if (this.nativeLoggerEnabled) {
      nativeLog?.(this.name, 'trace', message, ...args);
    }
  };

  time = <T,>(label: string, fn: () => T): T => {
    const start = Date.now();
    const result = fn();
    this.log(label, Date.now() - start);
    return result;
  };

  /** Logs the message to our internal files/tools, just doesn't print to the console. */
  fileOnly = (message: string, ...args: any[]) => {
    log(this.name, 'file-only', message, ...args);
    if (this.nativeLoggerEnabled) {
      nativeLog?.(this.name, 'file-only', message, ...args);
    }
  };
}
